<!--
  ConfirmChoice — the body of the app's shared confirmation.

  Optional icon-circle + title + optional explanation + two brand-blue "ja /
  nein" choice boxes. Extracted from ConfirmDeleteDialog so that every
  confirmation in the app looks identical whether or not it happens to be a
  delete, and whether or not it gets a Dialog of its own (never a red button —
  see MEMORY / design language).

  Three consumers today:
    - ConfirmDeleteDialog, which wraps this in its own Dialog;
    - TransferDataDialog, which shows it as a step inside a dialog that is
      already open, and so cannot nest a second <dialog>;
    - UnlinkDialog, which uses the `align="left"` / no-icon treatment.

  The two treatments differ only in framing. `center` (default) is the
  attention-grabbing one: a big blue icon circle over centred text. `left` is
  the quieter one from the unlink design — no circle, text ranged left like a
  paragraph — for a confirmation that needs to be *read* rather than reacted
  to. The choice boxes stay centred in both; they are the same control.

  Presentational only: the parent owns the decision. Pass `busy` while the
  action is in flight (dims and disables both choices) and `error` to surface a
  failure under the title.
-->
<template>
  <div class="confirm-choice" :class="`align-${align ?? 'center'}`">
    <span v-if="icon" class="icon-circle" aria-hidden="true">
      <img :src="icon" alt="" />
    </span>
    <Heading weight="h1">{{ title }}</Heading>
    <p v-if="body" class="note body">{{ body }}</p>
    <p v-if="error" class="note error">{{ error }}</p>
    <div class="confirm-choices">
      <button
        type="button"
        class="choice ja"
        :disabled="busy"
        :aria-label="confirmLabel ?? t('yes')"
        @click="emit('confirm')"
      >
        <span class="box"><img :src="checkmark" alt="" aria-hidden="true" /></span>
        <span class="label">{{ confirmLabel ?? t('yes') }}</span>
      </button>
      <button
        type="button"
        class="choice nein"
        :disabled="busy"
        :aria-label="cancelLabel ?? t('no')"
        @click="emit('cancel')"
      >
        <span class="box"><img :src="closeIcon" alt="" aria-hidden="true" /></span>
        <span class="label">{{ cancelLabel ?? t('no') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import Heading from '@/components/Heading.vue'

import checkmark from '@/assets/icon/checkmark.svg?no-inline'
import closeIcon from '@/assets/icon/close.svg?no-inline'

const { t } = useI18n()

defineProps<{
  /** Heading, e.g. "Übung löschen?". */
  title: string
  /**
   * The icon inside the blue circle; omit for no circle at all.
   *
   * Black-filled SVG: the circle tints source pixels to brand blue, so a
   * white-stroked icon (link.svg) will not work — which is the other reason
   * the unlink confirmation has no circle.
   */
  icon?: string
  /** Optional sentence under the title, for a consequence worth spelling out. */
  body?: string
  /** Text alignment of the title and body. Choices stay centred either way. */
  align?: 'center' | 'left'
  /** Affirmative label; defaults to the localized "ja" / "yes". */
  confirmLabel?: string
  /** Dismiss label; defaults to the localized "nein" / "no". */
  cancelLabel?: string
  /** True while the action is in flight — dims and disables both choices. */
  busy?: boolean
  /** Optional failure message shown under the title. */
  error?: string | null
}>()

const emit = defineEmits<{
  confirm: []
  cancel: []
}>()
</script>

<style scoped lang="css">
div.confirm-choice {
  display: flex;
  flex-direction: column;
  row-gap: 1rem;

  padding: 0.5rem 0;

  /* :deep() so the rule reaches Heading.vue's scoped <h1>. Dialog.vue gives
     a slotted h1 its own margins, which would double up on the row-gap. */
  :deep(h1) {
    margin: 0 !important;
  }

  &.align-center {
    align-items: center;

    /* Override Dialog.vue's default left-aligned h1 styling. */
    :deep(h1) {
      text-align: center !important;
    }

    p.note {
      text-align: center;
    }
  }

  /* The unlink treatment: text ranged left, nothing centred but the choices.
     The body also drops the dimming the centred variant gives it — with no
     icon circle to anchor the dialog this paragraph *is* the content, and
     greying it out made it read as fine print. */
  &.align-left {
    align-items: stretch;
    row-gap: 0.85rem;

    :deep(h1) {
      text-align: left !important;
    }

    p.note {
      text-align: left;
    }

    p.note.body {
      opacity: 1;
      /* Measure, not full bleed — the medium dialog is wide enough that an
         unbroken line would run past a comfortable reading length. */
      max-width: 36rem;
    }
  }

  span.icon-circle {
    width: 6.5rem;
    height: 6.5rem;

    display: flex;
    align-items: center;
    justify-content: center;

    border: 3px solid rgba(0, 142, 207, 1);
    border-radius: 50%;

    img {
      width: 2.75rem;
      height: 2.75rem;
      /* Tint black source pixels to brand blue — same filter chain as
         the card's trash button so the two icons match. */
      filter: invert(47%) sepia(82%) saturate(2900%) hue-rotate(174deg) brightness(96%)
        contrast(101%);
    }
  }
}

div.confirm-choices {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 2.5rem;

  margin-top: 0.5rem;
}

button.choice {
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 0.5rem;

  background: transparent;
  border: 0;
  padding: 0;

  cursor: pointer;

  span.box {
    width: 3rem;
    height: 3rem;

    display: flex;
    align-items: center;
    justify-content: center;

    background: rgba(0, 142, 207, 1);
    border-radius: 8px;
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);

    img {
      width: 1.25rem;
      height: 1.25rem;
      /* White icon over the blue box. */
      filter: invert(100);
    }
  }

  span.label {
    font-size: 1rem;
    font-weight: 600;
    color: var(--black);
  }

  &[disabled] {
    opacity: 0.5;
    pointer-events: none;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover span.box {
      box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
    }
  }
}

p.note.error {
  color: rgba(229, 57, 53, 1);
  font-size: 0.95rem !important;
  margin-bottom: 0;
}
/* Added with the extraction: the explanation some confirmations need and a
   delete never did. Quieter than the title, above the choices.

   Sizes itself explicitly, like p.note.error above, because Dialog.vue's
   `:slotted(p.note)` rule cannot reach here — ::slotted only matches the
   top-level slotted node, and these paragraphs are descendants of it. */
p.note.body {
  font-size: 0.95rem !important;
  line-height: 145%;
  opacity: 0.65;

  margin-bottom: 0;
}
</style>

<i18n>
{
  "de": {
    "yes": "ja",
    "no": "nein"
  },
  "en": {
    "yes": "yes",
    "no": "no"
  }
}
</i18n>
